import { useContext, useMemo } from 'react';

import { useCluster } from '@cast/shared-cluster-context-data-access';
import {
  buildAnalyzePrompt,
  useAnalyzeWithOpsPilot,
} from '@cast/features-organization-shared-opspilot';
import { attachTimeTicks } from 'utils/chart';

import { useRightsizingTimeRangeSelection } from '../../../../_hooks/useRightsizingTimeRangeSelection';
import type { TimeRangeSelection } from '../../../../_hooks/useCurrentWorkloadMetrics';
import { WorkloadDetailsContext } from '../../../_context';

type UseHpaChartDataArgs = {
  timeRangeSelection?: TimeRangeSelection;
};

export const useHpaChartData = ({
  timeRangeSelection,
}: UseHpaChartDataArgs = {}) => {
  const { metrics, workload, isFetching, error, refetch } = useContext(
    WorkloadDetailsContext
  );
  const rightsizingSelection = useRightsizingTimeRangeSelection();
  const { timeZone, getRange } = timeRangeSelection ?? rightsizingSelection;
  const { cluster } = useCluster();

  const podsChartData = useMemo(
    () => attachTimeTicks(metrics?.pods.podCount ?? [], 'timestamp'),
    [metrics?.pods.podCount]
  );

  const cpuChartData = useMemo(
    () => attachTimeTicks(metrics?.cpu.totalCpuCores ?? [], 'timestamp'),
    [metrics?.cpu.totalCpuCores]
  );

  const podsAnalyzeWithOpsPilot = useAnalyzeWithOpsPilot({
    buildPrompt: buildAnalyzePrompt({
      context: 'HPA pods metric',
      scopeName: cluster.name,
      getDefaultRange: getRange as () => { from: string; to: string },
    }),
  });

  const cpuAnalyzeWithOpsPilot = useAnalyzeWithOpsPilot({
    buildPrompt: buildAnalyzePrompt({
      context: 'HPA cpu metric',
      scopeName: cluster.name,
      getDefaultRange: getRange as () => { from: string; to: string },
    }),
  });

  return {
    metrics,
    workload,
    isFetching,
    error,
    refetch,
    timeZone,
    podsChartData,
    cpuChartData,
    podsAnalyzeWithOpsPilot,
    cpuAnalyzeWithOpsPilot,
  };
};
